import { type FC, memo, useMemo } from 'react';
import { Platform, StyleSheet, Text } from 'react-native';
import Animated, {
  type SharedValue,
  useAnimatedStyle,
  useDerivedValue,
} from 'react-native-reanimated';

import type { StyleProp, TextStyle } from 'react-native';

type AnimatedDigitProps = {
  index: number;
  count: SharedValue<number>;
  height: number;
  width: number;
  textStyle: StyleProp<TextStyle>;
  maxDigits: number;
};

/** Decimal place power for MSD-first columns: index 0 = highest place. Locale-proof (no string/padStart). */
const getDigitByIndex = ({
  digit_index,
  count,
  max_digits,
}: {
  digit_index: number;
  count: number;
  max_digits: number;
}) => {
  'worklet';
  const n = Math.floor(Math.abs(count));
  const power = max_digits - 1 - digit_index;
  if (power < 0) {
    return 0;
  }
  return Math.floor(n / Math.pow(10, power)) % 10;
};

const digitCount = (count: number) => {
  'worklet';
  const n = Math.max(0, Math.floor(Math.abs(count)));
  if (n === 0) {
    return 1;
  }
  return Math.floor(Math.log10(n)) + 1;
};

const IS_ANDROID = Platform.OS === 'android';

export const AnimatedDigit: FC<AnimatedDigitProps> = memo(
  ({ height, width, textStyle, index, count, maxDigits }) => {
    const digit = useDerivedValue(() => {
      return getDigitByIndex({
        digit_index: index,
        count: count.value,
        max_digits: maxDigits,
      });
    }, [index, maxDigits]);

    const is_visible = useDerivedValue(() => {
      const leading_zeros = maxDigits - digitCount(count.value);
      return index >= leading_zeros;
    }, [index, maxDigits]);

    const flattened_text_style = useMemo(() => StyleSheet.flatten(textStyle), [textStyle]);

    /** Instant digit settle — nested withTiming in derived/style stacks animations on long flings and can crash. */
    const r_style = useAnimatedStyle(() => {
      return {
        transform: [{ translateY: -height * digit.value }],
      };
    }, [height]);

    const r_container_style = useAnimatedStyle(() => {
      const show = is_visible.value;
      return {
        opacity: show ? 1 : 0,
        /** Collapse leading zeros so the viewport clips MSD→LSD correctly (avoids blank `5′ ″`). */
        width: show ? width : 0,
      };
    }, [width]);

    const font_size =
      typeof flattened_text_style?.fontSize === 'number' ? flattened_text_style.fontSize : 50;

    return (
      <Animated.View
        style={[
          {
            height,
            overflow: 'hidden',
          },
          r_container_style,
        ]}>
        <Animated.View style={[{ flexDirection: 'column' }, r_style]}>
          {new Array(10).fill(0).map((_, text_index) => (
            <Text
              key={text_index}
              allowFontScaling={false}
              style={{
                ...flattened_text_style,
                width,
                height,
                lineHeight: IS_ANDROID ? height : font_size * 1.08,
                textAlign: 'center',
                textAlignVertical: 'center',
                fontVariant: ['tabular-nums'],
                fontFamily: Platform.OS === 'ios' ? 'SF-Pro-Rounded-Bold' : undefined,
                fontWeight: Platform.OS === 'android' ? '700' : flattened_text_style?.fontWeight,
                ...(IS_ANDROID ? { includeFontPadding: false, paddingTop: 0, paddingBottom: 0 } : {}),
              }}>
              {text_index}
            </Text>
          ))}
        </Animated.View>
      </Animated.View>
    );
  },
);
